import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/ui-extra";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { toast } from "sonner";
import {
  Plus, Loader2, Copy, Trash2, KeyRound, Plug, ShieldCheck,
  AlertTriangle,
} from "lucide-react";

/**
 * ChatGPT Connector — each executive mints their own revocable connector
 * token (bound to their account + permissions) and pastes it into ChatGPT's
 * custom connector. Includes a step-by-step setup guide.
 */
export default function ConnectorPage() {
  const [info, setInfo] = useState(null);
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [label, setLabel] = useState("");
  const [creating, setCreating] = useState(false);
  const [freshToken, setFreshToken] = useState(null); // shown once
  const [freshUrl, setFreshUrl] = useState(null);      // personalized connector URL, shown once

  const load = async () => {
    setLoading(true);
    try {
      const [i, t] = await Promise.all([
        api.get("/mcp/connector/info"),
        api.get("/mcp/connector/tokens"),
      ]);
      setInfo(i.data);
      setTokens(t.data?.tokens || []);
    } catch (_e) {
      toast.error("Couldn't load connector settings");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const createToken = async () => {
    setCreating(true);
    try {
      const res = await api.post("/mcp/connector/tokens", { label: label.trim() });
      setFreshToken(res.data?.token || null);
      setFreshUrl(res.data?.connector_url || null);
      setLabel("");
      toast.success("Connector created — copy your URL now");
      load();
    } catch (_e) {
      toast.error("Could not create token");
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (id) => {
    if (!id) return;
    await api.delete(`/mcp/connector/tokens/${id}`);
    toast.success("Token revoked");
    load();
  };

  const copy = (text, msg = "Copied") => {
    try {
      navigator.clipboard.writeText(text);
      toast.success(msg);
    } catch (_e) {
      toast.error("Copy failed — select and copy manually");
    }
  };

  const mcpUrl = info?.mcp_url || "";
  const activeTokens = tokens.filter((t) => !t.revoked_at);

  return (
    <div data-testid="connector-page">
      <PageHeader
        eyebrow="Integrations"
        title="ChatGPT Connector"
        subtitle="Ask ChatGPT anything your role allows across the whole operation — horses & profiles (photos shown in the chat), health, sessions, seasons, rankings, injuries, invoices, payroll, costs and more — using your own account and permissions."
      />

      {/* Plan limitations callout */}
      <Card className="rounded-xl mb-6 border-[hsl(var(--ap-forest))]/30" data-testid="connector-plan-note">
        <CardContent className="pt-5 flex gap-3">
          <AlertTriangle className="h-5 w-5 text-[hsl(var(--ap-forest))] shrink-0 mt-0.5" />
          <div className="text-sm space-y-1" style={{ color: "var(--text-secondary)" }}>
            <p><strong>Before you start:</strong> custom connectors work in ChatGPT <strong>on the web</strong> (not the mobile apps), and need at least a <strong>Plus</strong> or <strong>Pro</strong> plan (Free isn't supported).</p>
            <p>On Plus/Pro the connector is <strong>read-only</strong>: ChatGPT can answer and summarise, and when you ask to schedule something it drafts the task and gives you a one-tap link to confirm it here in the app.</p>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* ── Left: tokens ───────────────────────────── */}
        <div className="space-y-6">
          <Card className="rounded-xl" data-testid="connector-create-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <KeyRound className="h-4 w-4 text-[hsl(var(--ap-forest))]" /> Your connector token
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
                Create a token, then paste it into ChatGPT when adding the connector. It's tied to
                your account, so ChatGPT only ever sees what your role allows. You can revoke it any time.
              </p>
              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <Label className="text-xs">Label (optional)</Label>
                  <Input
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    placeholder="e.g. My laptop ChatGPT"
                    data-testid="connector-token-label-input"
                  />
                </div>
                <Button onClick={createToken} disabled={creating} data-testid="connector-create-token-btn"
                  className="bg-[hsl(var(--ap-forest))] hover:bg-[hsl(var(--ap-forest))]/90">
                  {creating ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
                  Create token
                </Button>
              </div>

              {(freshToken || freshUrl) && (
                <div className="rounded-lg border border-[hsl(var(--ap-forest))]/40 bg-[hsl(var(--ap-forest))]/5 p-3 space-y-3" data-testid="connector-fresh-token">
                  <div className="flex items-center gap-2 text-sm font-medium text-[hsl(var(--ap-forest))]">
                    <ShieldCheck className="h-4 w-4" /> Copy your connector URL now — it won't be shown again
                  </div>
                  <div>
                    <Label className="text-xs">Your connector URL (paste into ChatGPT)</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <code className="flex-1 text-xs break-all rounded bg-background px-2 py-1.5 border" data-testid="connector-fresh-url-value">{freshUrl}</code>
                      <Button size="icon" variant="outline" onClick={() => copy(freshUrl, "Connector URL copied")} data-testid="connector-copy-fresh-url">
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    This URL contains your personal access key. Treat it like a password — anyone with it can read the
                    data your role allows. Revoke it below any time to disable it instantly.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="rounded-xl" data-testid="connector-tokens-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Active tokens</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <p className="text-sm text-muted-foreground py-4 text-center">Loading…</p>
              ) : activeTokens.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center" data-testid="connector-no-tokens">
                  No tokens yet. Create one above to connect ChatGPT.
                </p>
              ) : (
                <div className="space-y-2" data-testid="connector-tokens-list">
                  {activeTokens.map((t) => (
                    <div key={t.id} className="flex items-center justify-between gap-2 rounded-lg border p-3" data-testid={`connector-token-${t.id}`}>
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">{t.label || "ChatGPT connector"}</div>
                        <div className="text-xs text-muted-foreground">
                          {t.token_preview ? <code>{t.token_preview}</code> : null}
                          {t.last_used_at ? ` · last used ${new Date(t.last_used_at).toLocaleDateString()}` : " · never used"}
                        </div>
                      </div>
                      <ConfirmDialog
                        title="Revoke this token?"
                        description="ChatGPT connections using this token will stop working immediately. This can't be undone."
                        confirmLabel="Revoke"
                        destructive
                        testId={`connector-revoke-${t.id}`}
                        onConfirm={() => revoke(t.id)}
                      >
                        <Button size="icon" variant="ghost" data-testid={`connector-revoke-${t.id}`}
                          className="text-destructive hover:text-destructive">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </ConfirmDialog>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* ── Right: setup guide ─────────────────────── */}
        <Card className="rounded-xl" data-testid="connector-guide-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Plug className="h-4 w-4 text-[hsl(var(--ap-forest))]" /> Add it in ChatGPT (web)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm" style={{ color: "var(--text-secondary)" }}>
            <div className="rounded-lg border p-3 text-xs">
              Base server address: <code className="break-all">{mcpUrl || "—"}</code>
              <div className="mt-1 text-muted-foreground">
                Don't use this one directly — create a token on the left and paste the full
                <strong> connector URL </strong> it gives you (it adds your personal key to the end).
              </div>
            </div>
            <ol className="list-decimal pl-5 space-y-2">
              <li>In ChatGPT on the web, open <strong>Settings → Connectors</strong> and turn on <strong>Developer Mode</strong>.</li>
              <li>Choose <strong>Add custom MCP server / connector</strong> and give it a name (e.g. “AP MCP Tool”).</li>
              <li>For <strong>Server URL</strong>, paste your personal <strong>connector URL</strong> from the left (the one ending in your key).</li>
              <li>Set <strong>Authentication</strong> to <strong>“No authentication”</strong> — your key is already in the URL, so no separate login is needed.</li>
              <li>Save and let ChatGPT scan the tools. You'll get permission-scoped read access across the whole operation — horses &amp; profiles (with photos shown inline), health, dated sessions, seasons, rankings, rider activity, injuries, invoices, payroll, costs, staff, breeding, feed, tack, vehicles and the changes feed — plus <Badge variant="secondary">ask</Badge> and <Badge variant="secondary">draft_task</Badge>.</li>
              <li>Ask away — e.g. <em>“Show Bella's profile and photo”</em>, <em>“list every session for Bella during the Young Horse Season”</em>, or <em>“what does this week's spend look like across vet and feed?”</em></li>
              <li>To schedule, ask e.g. <em>“Book the vet for Bella tomorrow.”</em> ChatGPT drafts it and returns a link — tap it to confirm here.</li>
            </ol>
            <div className="rounded-lg border p-3 flex items-start gap-2">
              <ShieldCheck className="h-4 w-4 text-[hsl(var(--ap-forest))] shrink-0 mt-0.5" />
              <p className="text-xs">
                Everything ChatGPT reads is limited to your permissions, and every request is logged for oversight.
                Revoke your token here at any time to instantly cut off access.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
